import { fromBase64 } from "../../../utils/base64";
import { TDocumentData } from "../models/document";
import { TLinkedVrPerson, TLinkedVrsData } from "./linkedVrs";

export type EntityRepresentativeResponsible =
  | "this_is_parent_owner"
  | "parent_owner"
  | "person";

export type EntityRepresentativesPerson = {
  id: string;
  firstName: string;
  lastName: string;
  representative: boolean;
  ubo: boolean;
  responsible: EntityRepresentativeResponsible;
  email?: string;
  phone?: string;
  removed?: boolean;
};

export type EntityRepresentativesData = {
  people: EntityRepresentativesPerson[];
};

export type EntityRepresentativeReviewRow = EntityRepresentativesPerson & {
  fullName: string;
  contact: string;
  performerLabel: string;
  isSelfService: boolean;
  isRemoved: boolean;
  childVrId?: number;
  childVrHasEmail: boolean;
};

const RESPONSIBLE_LABELS: Record<
  EntityRepresentativeResponsible,
  string
> = {
  this_is_parent_owner: "This is me",
  parent_owner: "I will go through KYC for this user",
  person: "User will go through KYC process himself",
};

export function formatEntityRepresentativeResponsibleLabel(
  value: unknown
): string {
  return isResponsible(value) ? RESPONSIBLE_LABELS[value] : "";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isResponsible(
  value: unknown
): value is EntityRepresentativeResponsible {
  return (
    value === "this_is_parent_owner" ||
    value === "parent_owner" ||
    value === "person"
  );
}

function normalizeString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function parseEntityRepresentativesPayload(
  payload: unknown
): EntityRepresentativesData {
  if (!isRecord(payload) || !Array.isArray(payload.people)) {
    return { people: [] };
  }

  const people = payload.people
    .filter(isRecord)
    .map<EntityRepresentativesPerson>((person, index) => {
      const responsible = isResponsible(person.responsible)
        ? person.responsible
        : "parent_owner";

      return {
        id: normalizeString(person.id) || `entity-representative-${index}`,
        firstName: normalizeString(person.firstName),
        lastName: normalizeString(person.lastName),
        representative: person.representative === true,
        ubo: person.ubo === true,
        responsible,
        email: normalizeString(person.email) || undefined,
        phone: normalizeString(person.phone) || undefined,
        removed: person.removed === true || undefined,
      };
    });

  return { people };
}

export function parseEntityRepresentativesData(
  entry: TDocumentData | undefined
): EntityRepresentativesData {
  if (!entry) {
    return { people: [] };
  }

  const parsedFromField = parseEntityRepresentativesPayload(entry.data);
  if (parsedFromField.people.length > 0) {
    return parsedFromField;
  }

  if (!entry.dataBase64) {
    return parsedFromField;
  }

  try {
    const decoded = fromBase64(entry.dataBase64);
    const parsed = JSON.parse(decoded);
    return parseEntityRepresentativesPayload(parsed);
  } catch {
    return parsedFromField;
  }
}

export function getEntityRepresentativeRows(
  entry: TDocumentData | undefined,
  linkedVrs: TLinkedVrsData | null
): EntityRepresentativeReviewRow[] {
  const linkedByPersonId = new Map<string, TLinkedVrPerson>(
    linkedVrs?.persons.map((person) => [person.person_id, person]) ?? []
  );

  return parseEntityRepresentativesData(entry).people.map((person) => {
    const fullName = [person.firstName, person.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    const contact = [person.email, person.phone].filter(Boolean).join(", ");
    const linked = linkedByPersonId.get(person.id);

    return {
      ...person,
      fullName: fullName || "Unnamed",
      contact,
      performerLabel: RESPONSIBLE_LABELS[person.responsible],
      isSelfService: person.responsible === "person",
      isRemoved: person.removed === true,
      childVrId: linked?.verification_request_id,
      childVrHasEmail: linked?.has_email ?? false,
    };
  });
}

export function splitEntityRepresentativeRows(
  rows: EntityRepresentativeReviewRow[]
): {
  activeRows: EntityRepresentativeReviewRow[];
  removedRows: EntityRepresentativeReviewRow[];
} {
  return {
    activeRows: rows.filter((row) => !row.isRemoved),
    removedRows: rows.filter((row) => row.isRemoved),
  };
}
